import React, {
  useEffect,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Spin,
  Table,
  Col,
  Radio,
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CaretDownOutlined,
  CaretUpOutlined,
  ClearOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';

// Import Components:
import FormResponder from '../FormResponder';
import FormsTable from '../FormsTable';
import FormSearchInput from '../FormSearchInput';
import FormDetailView from '../CompletedForms/FormDetailView';

// Import Actions:
import {
  cancelForms,
  getAssignedForms,
  getAssignedToOthersForms,
  getFormById,
  hideAssignedToOthersForm,
  updateReadStatus,
} from '../state/forms.actions';

// Import Helpers:
import { getIdMap, includesTerm } from '../../helpers/helpers';
import { getAssignedFormColumns, getFormLogColumns } from '../FormColumns';
import {
  getFilters,
  handleFormClick,
  getFormDisplayFieldColumns,
  getFormListAndFilters,
  getAssignedToMyselfForms,
} from '../formHelpers';
import { parseFormResponseToReadableFormat } from '../ResponderHelpers';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import useToggle from '../../common/hooks/useToggle';
import useFormSearch from '../../common/hooks/useFormSearch';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import Permissions from '../../auth/Permissions';
import AssignedFormDrawer from './AssignedFormDrawer';
import {
  ASSIGNED_TO_MYSELF_TAB,
  ASSIGNED_TO_OTHERS_TAB,
} from '../FormsHome/formsHomeHelpers';
import OnTraccrRadioGroup from '../../common/inputs/OnTraccrRadioGroup';

/** Assigned Forms */
export default function AssignedForms({
  history,
  userId,
  isUserAssigned,
  onCardColumnClick,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const assignedForms = useSelector((state) => state.forms.assignedForms);
  const {
    manual: manualDrafts = {},
    assigned: assignedDrafts = {},
  } = useSelector((state) => state.forms.drafts);
  const customers = useSelector((state) => state.customers.customers);
  const projects = useSelector((state) => state.projects.projects);
  const templates = useSelector((state) => state.forms.templates);
  const { settings = {} } = useSelector((state) => state.settings.company);
  const users = useSelector((state) => state.users.users);
  const vendorMap = useSelector((state) => state.vendors.vendors);
  const formApprovals = useSelector((state) => state.forms.approvalSummary);
  const assignedFormTemplates = useSelector((state) => state.forms.assignedFormTemplates);
  const assignedToOthersForms = useSelector((state) => state.forms.assignedToOthersForms);

  const userMap = useMemo(() => getIdMap(users), [users]);

  const [showDetail, setShowDetail] = useState(false);
  const [assignedTableData, setAssignedTableData] = useState([]);
  const [editTableData, setEditTableData] = useState([]);
  const [assignedForm, setAssignedForm] = useState({ });
  const [selectedIsEdit, setSelectedIsEdit] = useState(false);
  const [selectedIsResubmit, setSelectedIsResubmit] = useState(false);
  const [searchStr, setSearchStr] = useState();
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [cancelRowKeys, setCancelRowKeys] = useState([]);
  const [showFormDetail, setShowFormDetail] = useState(false);
  const [loadingStates, setLoadingStates] = useState({});
  const [tab, setTab] = useState(ASSIGNED_TO_MYSELF_TAB);
  const [showAssignedFormDrawer, setShowAssignedFormDrawer] = useState(false);
  const [hasLoadedAssignedForms, setHasLoadedAssignedForms] = useState(false);

  const hasAssignedToOthersPermission = Permissions.has('FORMS_WRITE');

  const { isToggled: isExpanded, toggle: toggleExpanded } = useToggle(true);

  const searchFormIds = useMemo(() => {
    if (tab === ASSIGNED_TO_OTHERS_TAB) {
      return assignedToOthersForms.map(({ id }) => id);
    }
    const formIds = new Set();
    assignedTableData.forEach(({ id }) => {
      if (id) formIds.add(id);
    });
    editTableData.forEach(({ id }) => {
      if (id) formIds.add(id);
    });
    return Array.from(formIds);
  }, [tab, assignedTableData, editTableData, assignedToOthersForms]);

  const searchDraftIds = useMemo(() => {
    if (tab !== ASSIGNED_TO_MYSELF_TAB) return [];
    const draftIds = new Set();
    Object.values(manualDrafts).forEach((draft) => {
      if (draft?.id) draftIds.add(draft.id);
    });
    Object.values(assignedDrafts).forEach((draft) => {
      if (draft?.id) draftIds.add(draft.id);
    });
    return Array.from(draftIds);
  }, [tab, manualDrafts, assignedDrafts]);

  const searchParams = useMemo(() => ({
    searchTerm: searchStr,
    formIds: searchFormIds,
    draftIds: searchDraftIds,
  }), [searchStr, searchFormIds, searchDraftIds]);
  const {
    searchSet,
    searchLoading,
  } = useFormSearch(searchParams);

  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);

  const clearFormSelection = useCallback(() => setShowDetail(false), []);

  const onFormClick = useCallback(async (record) => {
    if (isUserAssigned || tab === ASSIGNED_TO_OTHERS_TAB) {
      dispatch(getFormById(record.id, { getRecentDraft: true }));
      setShowFormDetail(true);
    } else {
      await handleFormClick({
        record,
        dispatch,
        loading,
        setLoading,
        setAssignedForm,
        setShowDetail,
        setSelectedIsEdit,
        setSelectedIsResubmit,
      });
    }
  }, [loading, isUserAssigned, tab]);

  const onCancelForms = useCallback(() => {
    CustomConfirmModal({
      title: 'Cancel Selected Forms?',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: async () => {
        await dispatch(cancelForms(cancelRowKeys));
      },
    });
  }, [cancelRowKeys]);

  const onHideAssignedFormClick = useCallback((formId) => {
    CustomConfirmModal({
      title: 'Hide Assigned Form?',
      onOk: () => {
        dispatch(hideAssignedToOthersForm(formId));
      },
      content: 'This form will no longer appear in the Assigned to Others tab and cannot be unhidden.',
    });
  }, []);

  useEffect(() => {
    if (tab !== ASSIGNED_TO_OTHERS_TAB) return;
    setLoading(true);
    dispatch(getAssignedToOthersForms());
    setLoading(false);
  }, [tab]);

  useEffect(() => {
    if (tab !== ASSIGNED_TO_MYSELF_TAB || hasLoadedAssignedForms) return;
    setAssignedTableData([]);
    setEditTableData([]);
    setLoading(true);
    dispatch(getAssignedForms(userId)).finally(() => {
      setLoading(false);
      setHasLoadedAssignedForms(true);
    });
  }, [userId, tab, hasLoadedAssignedForms]);

  useEffect(() => {
    if (tab !== ASSIGNED_TO_MYSELF_TAB) return;
    if (assignedForms.length) {
      setAssignedTableData(assignedForms.filter(({ editable }) => !editable));
      setEditTableData(assignedForms.filter(({ editable }) => !!editable));
    } else {
      setAssignedTableData([]);
      setEditTableData([]);
    }
  }, [assignedForms, tab]);

  const onSearch = useCallback((e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    setSearchStr(value);
  }, []);

  const {
    assignedToMeForms,
    assignedCustomerFilter,
    assignedProjectFilter,
    assignedCardFilters,
  } = useMemo(() => {
    const fullData = getAssignedToMyselfForms({
      assignedForms,
      assignedDrafts,
      manualDrafts,
      templates,
      projectIdMap,
      customerIdMap: customers,
      includeManualDrafts: !isUserAssigned,
    });

    const filteredAndSorted = fullData
      .filter(({
        id,
        name,
        number,
        projectNames,
        customer,
        state,
      }) => (
        (!searchStr || searchSet?.has(id))
        || (name && includesTerm(name, searchStr))
        || (number && includesTerm(number.toString(), searchStr))
        || (projectNames && includesTerm(projectNames, searchStr))
        || (customer && includesTerm(customer, searchStr))
        || (state && includesTerm(state, searchStr))));

    const nameSet = new Set();

    const cardFilters = filteredAndSorted.reduce((acc, form) => {
      if (form.card && !nameSet.has(form.card)) {
        nameSet.add(form.card);
        acc.push({ text: form.card, value: form.card });
      }
      return acc;
    }, []);

    return {
      assignedToMeForms: filteredAndSorted,
      assignedCustomerFilter: getFilters({ data: filteredAndSorted, idMap: customers, key: 'customers' }),
      assignedProjectFilter: getFilters({
        data: filteredAndSorted,
        idMap: projectIdMap,
        key: 'projects',
      }),
      assignedCardFilters: cardFilters,
    };
  }, [
    assignedForms,
    assignedDrafts,
    manualDrafts,
    templates,
    projectIdMap,
    customers,
    searchStr,
    searchSet,
    isUserAssigned,
  ]);

  const {
    formList: assignedToOthersFormsParsed,
    vendorFilters,
    customerFilters,
    projectFilters,
    userFilters,
    templateFilters,
    statusFilters,
    cardFilters,
  } = useMemo(() => getFormListAndFilters({
    forms: assignedToOthersForms,
    isPayable: false,
    userMap,
    vendorMap,
    projectIdMap,
    customers,
  }), [assignedToOthersForms, userMap, vendorMap, projectIdMap, customers]);

  useEffect(() => {
    if (assignedToMeForms.length && !Object.keys(templates).length) {
      setTableLoading(true);
    } else {
      setTableLoading(false);
    }
  }, [templates, assignedToMeForms]);

  const handleToggleReadStatus = useCallback(async (formId, isRead, onlyInFormDrafts) => {
    setLoadingStates((prev) => ({ ...prev, [formId]: true }));
    await dispatch(updateReadStatus(formId, isRead, 'assigned', onlyInFormDrafts));
    setLoadingStates((prev) => ({ ...prev, [formId]: false }));
  }, []);

  const assignedTemplateSet = useMemo(() => (
    new Set(assignedFormTemplates.map((template) => template.id))
  ), [assignedFormTemplates]);

  // Get columns:
  // -----------------------------------------------------
  const assignedColumns = useMemo(() => {
    if (tab === ASSIGNED_TO_MYSELF_TAB) {
      return getAssignedFormColumns({
        projectFilters: assignedProjectFilter,
        customerFilters: assignedCustomerFilter,
        projectIdMap,
        settings,
        cardFilters: assignedCardFilters,
        t,
        onCardColumnClick,
        handleToggleReadStatus,
        loadingStates,
      });
    }

    return getFormLogColumns({
      userMap,
      vendorMap,
      projectIdMap,
      formApprovals,
      templateFilters,
      statusFilters,
      userFilters,
      vendorFilters,
      customerFilters,
      projectFilters,
      cardFilters,
      isPayable: false,
      onAddInvoiceClick: null,
      onPOClose: null,
      onCopy: null,
      settings,
      t,
      assignedTemplateSet,
      onCardColumnClick,
      onHideAssignedFormClick,
    });
  }, [
    tab,
    assignedProjectFilter,
    assignedCustomerFilter,
    projectIdMap,
    settings,
    assignedCardFilters,
    t,
    onCardColumnClick,
    handleToggleReadStatus,
    loadingStates,
    userMap,
    vendorMap,
    formApprovals,
    templateFilters,
    statusFilters,
    userFilters,
    vendorFilters,
    customerFilters,
    projectFilters,
    cardFilters,
    assignedTemplateSet,
    onHideAssignedFormClick,
  ]);

  const relevantTemplates = useMemo(() => {
    const relevantTemplateMap = {};
    const relevantTemplateIds = new Set();
    assignedToMeForms.forEach(({
      templateId,
    }) => {
      if (!relevantTemplateIds.has(templateId)) {
        relevantTemplateIds.add(templateId);

        const relevantTemplate = templates[templateId];
        if (!relevantTemplate) return;

        let relevantSections = [];
        try {
          const parsedTemplateData = JSON.parse(relevantTemplate?.jsonFormData);
          relevantSections = parsedTemplateData?.sections ?? [];
        } catch (err) {
          // do nothing
        }

        const fieldMap = relevantSections?.reduce((acc, { fields = [] }) => {
          fields.forEach((field) => {
            acc[field.id] = field;
          });
          return acc;
        }, {});

        relevantTemplateMap[templateId] = {
          ...relevantTemplate,
          sections: relevantSections,
          fieldMap,
          columns: getFormDisplayFieldColumns({ relevantTemplate, fieldMap }),
        };
      }
    });

    return relevantTemplateMap;
  }, [assignedToMeForms, templates]);

  const rowExpandable = useCallback((record) => {
    const relevantTemplate = relevantTemplates[record.templateId];
    return relevantTemplate?.displayFields?.length;
  }, [relevantTemplates]);

  const expandedRowRender = useCallback((record) => {
    const relevantTemplate = relevantTemplates[record.templateId];
    const formResponses = parseFormResponseToReadableFormat({
      templateSections: relevantTemplate?.sections ?? [],
      responseSections: record?.data?.sections ?? [],
      projectIdMap,
      settings,
      enableTable: true,
      id: `${record.id}-expanded`,
    });

    return (
      <Table
        columns={relevantTemplate?.columns ?? []}
        dataSource={[formResponses]}
        pagination={false}
        size="small"
        bordered
        rowKey="id"
      />
    );
  }, [relevantTemplates]);

  useEffect(() => {
    if (tab !== ASSIGNED_TO_MYSELF_TAB) return;
    if (isExpanded) {
      setExpandedRowKeys(assignedToMeForms.map(({ id }) => id));
    } else {
      setExpandedRowKeys([]);
    }
  }, [assignedToMeForms, isExpanded, tab]);

  const onExpand = useCallback((event, { id }) => {
    if (!event) {
      setExpandedRowKeys(expandedRowKeys.filter((key) => key !== id));
      return;
    }

    setExpandedRowKeys([...expandedRowKeys, id]);
  }, [expandedRowKeys]);

  const expandable = useMemo(() => ({
    defaultExpandAllRows: true,
    rowExpandable,
    expandedRowRender,
    expandedRowKeys,
    onExpand,
    columnWidth: 50,
  }), [rowExpandable, expandedRowRender, expandedRowKeys, onExpand]);

  const onCancelRowChange = useCallback((newSelectedRowKeys) => {
    setCancelRowKeys(newSelectedRowKeys);
  }, []);

  const onHeaderRow = useCallback(() => {
    if (!settings?.uxRedesignPt1) return null;
    return {
      className: 'ontraccr-table-header',
    };
  }, [settings]);

  const rowSelectionProps = useMemo(() => {
    if (!isUserAssigned) return {};

    return {
      rowSelection: {
        selectedRowKeys: cancelRowKeys,
        hideSelectAll: true,
        onChange: onCancelRowChange,
        getCheckboxProps: () => ({
          className: settings?.uxRedesignPt1 ? 'ontraccr-checkbox' : '',
        }),
      },
    };
  }, [onCancelRowChange, isUserAssigned, cancelRowKeys, settings]);

  const formsToUse = useMemo(() => {
    if (loading) return [];
    if (tab === ASSIGNED_TO_MYSELF_TAB) {
      return assignedToMeForms;
    }
    return assignedToOthersFormsParsed;
  }, [assignedToMeForms, assignedToOthersFormsParsed, tab, loading]);

  const hasCancelForms = !!cancelRowKeys?.length;

  const tabFilterOptions = useMemo(() => {
    if (!hasAssignedToOthersPermission || isUserAssigned) return [];
    return [
      {
        label: 'Assigned to me',
        value: ASSIGNED_TO_MYSELF_TAB,
        count: assignedToMeForms.length,
      },
      {
        label: 'Assigned to others',
        value: ASSIGNED_TO_OTHERS_TAB,
        count: assignedToOthersFormsParsed.length,
      },
    ];
  }, [
    hasAssignedToOthersPermission,
    isUserAssigned,
    assignedToMeForms.length,
    assignedToOthersFormsParsed.length,
  ]);

  return (
    <div>
      <Row className="forms-list-header" justify="space-between" align="middle">
        <Col>
          <FormSearchInput
            searchStr={searchStr}
            searchLoading={searchLoading}
            onSearch={onSearch}
            placeholder="Search assigned forms..."
            testId="formsAssignedSearch"
          />

          {isUserAssigned && (
            <OnTraccrButton
              title="Cancel Selected"
              disabled={!hasCancelForms}
              onClick={onCancelForms}
              icon={<ClearOutlined />}
            />
          )}
        </Col>
        <Col style={{ display: 'flex', alignItems: 'center' }}>
          <OnTraccrRadioGroup
            value={tab}
            size="medium"
            buttonStyle="outline"
            className="forms-home-tab-toggle"
            onChange={(newTab) => setTab(newTab)}
            style={{ marginRight: 10 }}
          >
            {tabFilterOptions.map(({ label, value, count }) => (
              <Radio.Button key={value} value={value}>
                {label}
                {' '}
                <span className="forms-home-tab-toggle-count">{count}</span>
              </Radio.Button>
            ))}
          </OnTraccrRadioGroup>
          {tab === ASSIGNED_TO_MYSELF_TAB && (
            <OnTraccrButton
              title={isExpanded ? 'Collapse all' : 'Expand all'}
              type="secondary"
              onClick={toggleExpanded}
              icon={isExpanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
              iconLeft
              className="forms-subpage-action-button"
            />
          )}
          {tab === ASSIGNED_TO_OTHERS_TAB && (
            <OnTraccrButton
              title="Assign Form"
              type="secondary"
              onClick={() => setShowAssignedFormDrawer(true)}
              icon={<PlusOutlined />}
              iconLeft
            />
          )}
        </Col>
      </Row>
      <div>
        <FormsTable
          data={formsToUse}
          columns={assignedColumns}
          onClickRow={onFormClick}
          rowKey="id"
          expandable={expandable}
          loading={tableLoading}
          rowSelectionProps={rowSelectionProps}
          onHeaderRow={onHeaderRow}
        />
        <FormDetailView
          userMap={userMap}
          visible={showFormDetail}
          onClose={() => setShowFormDetail(false)}
          startOnFields
        />
        <FormResponder
          visible={showDetail}
          onClose={clearFormSelection}
          history={history}
          redirectUrl="assigned"
          assignedForm={assignedForm}
          isOnEditStep={selectedIsEdit}
          isResubmit={selectedIsResubmit}
        />
        <AssignedFormDrawer
          visible={showAssignedFormDrawer}
          onClose={() => setShowAssignedFormDrawer(false)}
        />
      </div>
      {loading && (
        <Row justify="center" align="middle" className="form-loading-container">
          <Spin size="large" />
        </Row>
      )}
    </div>
  );
}

AssignedForms.propTypes = {
  userId: PropTypes.string,
  isUserAssigned: PropTypes.bool,
  onCardColumnClick: PropTypes.func.isRequired,
};

AssignedForms.defaultProps = {
  userId: null,
  isUserAssigned: false,
};
